/**
 * API client for FORGE Portal V2
 * Connects to the real Forge backend via WebObjects endpoints.
 */

// Derive the WebObjects base path from current URL
function getBasePath() {
  const path = window.location.pathname
  const match = path.match(/^(.*\/WebObjects\/forge\.woa)/)
  return match ? match[1] : ''
}

const WO_BASE = getBasePath()
const API_BASE = `${WO_BASE}/wa/appApi`

// Token management
function getToken() {
  return localStorage.getItem('forge_v2_session_token')
}

function setToken(token) {
  if (token) {
    localStorage.setItem('forge_v2_session_token', token)
  } else {
    localStorage.removeItem('forge_v2_session_token')
  }
}

function getStoredClient() {
  try {
    const data = localStorage.getItem('forge_v2_client')
    return data ? JSON.parse(data) : null
  } catch {
    return null
  }
}

function setStoredClient(client) {
  if (client) {
    localStorage.setItem('forge_v2_client', JSON.stringify(client))
  } else {
    localStorage.removeItem('forge_v2_client')
  }
}

function clearAuth() {
  localStorage.removeItem('forge_v2_session_token')
  localStorage.removeItem('forge_v2_client')
}

// Clear all client-specific cached data when switching accounts
// Keys are stored both with and without the storage module's "f_" prefix
function clearClientCache() {
  const keysToRemove = []
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i)
    if (key && (
      key.startsWith('f_') ||
      key.startsWith('forge_') ||
      key.startsWith('mc_') || key.startsWith('fl_') || key.startsWith('mp_') ||
      key.startsWith('wt_') || key.startsWith('wh_') || key.startsWith('sc_') ||
      key.startsWith('snap_') || key.startsWith('coach_') ||
      key === 'cd' || key === 'active_session' || key === 'onboarded' ||
      key === 'ob_data'
    )) {
      keysToRemove.push(key)
    }
  }
  keysToRemove.forEach(k => localStorage.removeItem(k))
}

// Full account switch: clear auth + all cached data
function switchAccount() {
  clearAuth()
  clearClientCache()
}

// Auto-recover workout weights from localStorage before clearing cache
// Runs with the CURRENT auth token (still valid) before account switch
async function autoRecoverWorkoutWeights() {
  const whKeys = []
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i)
    // Storage module prefixes keys with "f_", so workout history is "f_wh_*"
    if (key && (key.startsWith('wh_') || key.startsWith('f_wh_'))) whKeys.push(key)
  }
  if (whKeys.length === 0) return

  const exercises = []
  for (const key of whKeys) {
    try {
      const session = JSON.parse(localStorage.getItem(key))
      if (!session || !session.exercises) continue
      for (const ex of session.exercises) {
        if (!ex.sets) continue
        for (let s = 0; s < ex.sets.length; s++) {
          const set = ex.sets[s]
          if (set.weight && set.weight > 0) {
            exercises.push({
              exerciseName: ex.name,
              setNumber: s + 1,
              weight: set.weight,
              reps: set.reps,
              timestamp: session.timestamp,
            })
          }
        }
      }
    } catch (e) { /* skip corrupt entries */ }
  }

  if (exercises.length > 0) {
    await apiRequest('backfillWorkoutWeights', {
      method: 'POST',
      body: JSON.stringify({ exercises }),
    })
  }
}

// Generic API request with auth and retry logic
async function apiRequest(action, options = {}, retries = 2) {
  const url = `${API_BASE}?action=${action}`
  const token = getToken()

  const headers = {
    'Content-Type': 'application/json',
    ...(token && { Authorization: `Bearer ${token}` }),
    ...options.headers,
  }

  let response
  try {
    response = await fetch(url, {
      ...options,
      headers,
    })
  } catch (networkError) {
    // Network failure — retry with exponential backoff
    if (retries > 0) {
      const delay = (3 - retries) * 1000 // 1s, 2s
      await new Promise(resolve => setTimeout(resolve, delay))
      return apiRequest(action, options, retries - 1)
    }
    throw new Error('Network error — check your connection and try again')
  }

  // Check status BEFORE parsing JSON to avoid crash on non-JSON error responses
  if (!response.ok) {
    let errorMsg = 'Request failed'
    try {
      const errorData = await response.json()
      errorMsg = errorData.error || errorMsg
    } catch {
      // Response body was not JSON (e.g., HTML error page)
    }
    throw new Error(errorMsg)
  }

  return response.json()
}

// Auth API
export const auth = {
  async login(email, password) {
    const prevClient = getStoredClient()
    const data = await apiRequest('login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    })
    if (data.needsSetup) {
      const err = new Error('NEEDS_SETUP')
      err.needsSetup = true
      err.hasPhone = data.hasPhone
      err.maskedPhone = data.maskedPhone
      throw err
    }
    if (data.sessionToken) {
      // If switching to a different client, recover weights then clear cache
      if (!prevClient?.id || !data.client?.id || prevClient.id !== data.client.id) {
        try { await autoRecoverWorkoutWeights() } catch (e) { /* best effort */ }
        clearClientCache()
      }
      setToken(data.sessionToken)
      setStoredClient(data.client)
    }
    return data
  },

  async loginWithToken(token) {
    // Before clearing data, auto-recover any workout weights from localStorage
    // This must happen BEFORE switchAccount() wipes wh_* keys
    if (getToken()) {
      try {
        await autoRecoverWorkoutWeights()
      } catch (e) {
        console.warn("[Forge] auto-recovery before login:", e)
      }
    }
    // CRITICAL: Clear all previous auth + cached data before switching clients
    switchAccount()
    const data = await apiRequest('login', {
      method: 'POST',
      body: JSON.stringify({ token }),
    })
    if (data.sessionToken) {
      setToken(data.sessionToken)
      setStoredClient(data.client)
    }
    return data
  },

  async getSession() {
    return apiRequest('session')
  },

  async getChatToken() {
    return apiRequest('chatToken')
  },

  async requestPasswordReset(email) {
    return apiRequest('requestPasswordReset', {
      method: 'POST',
      body: JSON.stringify({ email }),
    })
  },

  async resetPassword(token, newPassword) {
    const data = await apiRequest('resetPassword', {
      method: 'POST',
      body: JSON.stringify({ token, newPassword }),
    })
    if (data.sessionToken) {
      setToken(data.sessionToken)
      setStoredClient(data.client)
    }
    return data
  },

  async trainerLogin(email, password) {
    const data = await apiRequest('trainerLogin', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    })
    return data
  },

  async sendSetupCode(email, phone) {
    const payload = { email }
    if (phone) payload.phone = phone
    return apiRequest('sendSetupCode', {
      method: 'POST',
      body: JSON.stringify(payload),
    })
  },

  async setupPassword(email, code, password) {
    const data = await apiRequest('setupPassword', {
      method: 'POST',
      body: JSON.stringify({ email, code, password }),
    })
    if (data.sessionToken) {
      setToken(data.sessionToken)
      setStoredClient(data.client)
    }
    return data
  },

  async changePassword(currentPassword, newPassword) {
    return apiRequest('changePassword', {
      method: 'POST',
      body: JSON.stringify({ currentPassword, newPassword }),
    })
  },

  logout() {
    clearAuth()
  },

  getToken,
  getStoredClient,
  setStoredClient,
  clearAuth,
}

// Client Profile API
export const profile = {
  async update(data) {
    const result = await apiRequest('updateClientProfile', {
      method: 'POST',
      body: JSON.stringify(data),
    })
    if (result.client) {
      setStoredClient(result.client)
    }
    return result
  },
}

// Settings API
export const settings = {
  async update(data) {
    const result = await apiRequest('updateClientSettings', {
      method: 'POST',
      body: JSON.stringify(data),
    })
    if (result.client) {
      setStoredClient(result.client)
    }
    return result
  },
}

// Dashboard API
export const dashboard = {
  async getData() {
    return apiRequest('clientDashboard')
  },
}

// Workouts API
export const workouts = {
  async list() {
    return apiRequest('clientWorkouts')
  },

  async get(id) {
    return apiRequest(`clientWorkout&id=${id}`)
  },

  async start(assignedWorkoutId) {
    return apiRequest('startWorkout', {
      method: 'POST',
      body: JSON.stringify({ assignedWorkoutId }),
    })
  },

  async logExercise(data) {
    return apiRequest('logExercise', {
      method: 'POST',
      body: JSON.stringify(data),
    })
  },

  async complete(workoutLogId, rating, notes) {
    return apiRequest('completeWorkout', {
      method: 'POST',
      body: JSON.stringify({ workoutLogId, rating, notes }),
    })
  },
}

// Stats API
export const stats = {
  async list() {
    // Client-facing: use dashboard for latest stats, or recovery logs for history
    return apiRequest('clientDashboard')
  },

  async create(data) {
    // Log recovery/check-in data (sleep, energy, stress, weight, measurements)
    return apiRequest('logRecovery', {
      method: 'POST',
      body: JSON.stringify(data),
    })
  },

  async history(limit = 30) {
    // Fetch check-in history from database
    return apiRequest(`clientRecoveryLogs&limit=${limit}`)
  },
}

// Meals API
export const meals = {
  async getCurrentPlan() {
    return apiRequest('clientMealPlan')
  },

  async getLogs(days = 7, limit = 50) {
    return apiRequest(`clientMealLogs&days=${days}&limit=${limit}`)
  },

  async logMeal(data) {
    return apiRequest('logMeal', {
      method: 'POST',
      body: JSON.stringify(data),
    })
  },

  /**
   * Analyze a food photo via Claude Vision and return estimated macros.
   * @param {string} photoBase64 — raw base64 (no data: prefix)
   * @param {string} mimeType — e.g. "image/jpeg"
   */
  async analyzeFoodPhoto(photoBase64, mimeType = 'image/jpeg') {
    return apiRequest('analyzeFoodPhoto', {
      method: 'POST',
      body: JSON.stringify({ photo: photoBase64, photoMimeType: mimeType }),
    })
  },
}

// Photos API
export const photos = {
  async list() {
    return apiRequest('clientPhotos')
  },

  async upload(data) {
    return apiRequest('uploadPhoto', {
      method: 'POST',
      body: JSON.stringify(data),
    })
  },
}

// Sessions API
export const sessions = {
  async list(params = {}) {
    let action = 'clientSessions'
    if (params.start) action += `&start=${params.start}`
    if (params.end) action += `&end=${params.end}`
    return apiRequest(action)
  },
}

// Recovery API
export const recovery = {
  async log(data) {
    return apiRequest('logRecovery', {
      method: 'POST',
      body: JSON.stringify(data),
    })
  },
}

// Daily Tracking API (water, supplements, meals)
export const dailyTracking = {
  async get(date) {
    const dateParam = date || new Date().toISOString().split('T')[0]
    return apiRequest(`getDailyTracking&date=${dateParam}`)
  },

  async update(data) {
    return apiRequest('updateDailyTracking', {
      method: 'POST',
      body: JSON.stringify(data),
    })
  },
}

// Cardio API
export const cardio = {
  async log(data) {
    return apiRequest('logCardio', {
      method: 'POST',
      body: JSON.stringify(data),
    })
  },

  async list(params = {}) {
    let action = 'clientCardioLogs'
    if (params.date) action += `&date=${params.date}`
    else if (params.days) action += `&days=${params.days}`
    return apiRequest(action)
  },
}

// Chat API
export const chat = {
  async getSuggestions() {
    return apiRequest('chatSuggestions')
  },

  async getHistory() {
    return apiRequest('clientChatHistory')
  },

  async flagMessage(messageId) {
    return apiRequest('flagMessage', {
      method: 'POST',
      body: JSON.stringify({ messageId }),
    })
  },
}

// Reports API
export const reports = {
  async getProgressReport(days = 30) {
    return apiRequest(`progressReport&days=${days}`)
  },
}

// Announcements API
export const announcementsApi = {
  async getUnread() {
    return apiRequest('clientAnnouncements')
  },
  async markRead(id) {
    return apiRequest('markAnnouncementRead', {
      method: 'POST',
      body: JSON.stringify({ id }),
    })
  },
}

// Feedback / Bug Report API
export const feedbackApi = {
  async submit({ title, description, feedbackType, area }) {
    return apiRequest('clientFeedback', {
      method: 'POST',
      body: JSON.stringify({ title, description, feedbackType, area }),
    })
  },
}

// Push Notifications API
export const pushNotifications = {
  async getVapidKey() {
    return apiRequest('getVapidPublicKey')
  },

  async subscribe(subscription) {
    const p256dh = btoa(String.fromCharCode(...new Uint8Array(subscription.getKey('p256dh'))))
    const auth = btoa(String.fromCharCode(...new Uint8Array(subscription.getKey('auth'))))
    return apiRequest('savePushSubscription', {
      method: 'POST',
      body: JSON.stringify({
        endpoint: subscription.endpoint,
        p256dh,
        auth,
        userAgent: navigator.userAgent,
      }),
    })
  },

  async unsubscribe(endpoint) {
    return apiRequest('deletePushSubscription', {
      method: 'POST',
      body: JSON.stringify({ endpoint }),
    })
  },
}

// Subscriptions API
export const subscriptions = {
  async checkout(planId) {
    return apiRequest('subscriptionCheckout', { method: 'POST', body: JSON.stringify({ planId }) })
  },
  async current() { return apiRequest('clientSubscription') },
  async plans() { return apiRequest('subscriptionPlans') },
}

// Build WebSocket URL for chat
export function getChatWebSocketUrl(token) {
  const host = window.location.host
  const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:'
  return `${protocol}//${host}/client-chat?token=${token}`
}
